import { Box, Container, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DropzoneLibrary from 'react-dropzone-uploader';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import profile from '../../api/profile';
import ImageCropper from './CropImage';
import {
  textColors,
  mainColors,
  secondaryColors
} from '../../theme/themeConfig';

interface Props {
  callApiOnComplete?: boolean;
  disabledDrop?: boolean;
  filesList?: Array<File>;
  isMobile: boolean;
  multiple?: boolean;
  parent: string;
  setFiles: React.Dispatch<React.SetStateAction<any>>;
  type: string;
}

const Dropzone: React.FC<Props> = function Dropzone({
  disabledDrop,
  filesList,
  type,
  multiple,
  isMobile,
  parent,
  setFiles,
  callApiOnComplete = true
}) {
  const { t } = useTranslation();
  const [content, setContent] = useState<Array<File>>([]);
  const [intialContent, setIntialContent] = useState<Array<File>>([]);
  const [letContent, setLetContent] = useState<boolean>(false);
  const dropzoneRef = useRef<any>(null);

  useEffect(() => {
    if (filesList && filesList.length > 0 && !letContent) {
      setLetContent(true);
      const auxInitialContent = filesList.map((contentItem: any) => {
        let auxType = contentItem?.type || contentItem?.mimeType || '';

        if (contentItem?.type?.includes('image') && parent === 'lead') {
          auxType = 'application/pdf';
        }

        return new File(
          [contentItem],
          contentItem.fileOriginalName || contentItem.name,
          {
            type: auxType
          }
        );
      });

      setIntialContent(auxInitialContent);
    } else {
      setIntialContent([]);
    }

    setContent(filesList || []);
  }, [filesList]);

  const inputState = () => {
    switch (type) {
      case 'image':
        if (parent === 'company' || parent === 'profile') {
          const inputName =
            parent === 'profile'
              ? `${t('PROFILE.picture')}`
              : `${t('COMPANY_SETTINGS.logo')}`;

          return (
            <Container
              key="dropzone-type-image-container"
              sx={{
                color: 'primary.300',
                display: 'grid',
                gridTemplateColumns: '35% 70%',
                marginLeft: '50px'
              }}
            >
              <Box
                key="dropzone-container-icon-box"
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center'
                }}
              >
                {isMobile ? null : <Typography>{inputName}</Typography>}
                <i
                  className="custom-icon icon-photo-upload"
                  style={{
                    fontSize: 60,
                    fontWeight: 'normal',
                    marginBottom: 1
                  }}
                />
              </Box>
              <Box
                key="dropzone-container-inputName-box"
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <Typography
                  sx={{
                    justifyContent: 'center',
                    ml: 4,
                    mr: 4,
                    textAlign: 'center'
                  }}
                  variant={isMobile ? 'body_100' : 'primary'}
                >
                  {`${t('GENERAL.dragNDrop', [inputName])}`}
                </Typography>
              </Box>
            </Container>
          );
        }
        if (parent === 'taskDetails' || parent === 'generalImage') {
          return (
            <Container
              key="dropzone-type-image-taskDetails-container"
              sx={{
                display: 'flex',
                width: '100%'
              }}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flex: 1,
                  justifyContent: 'center',
                  margin: 'auto'
                }}
              >
                <AddPhotoAlternateOutlinedIcon
                  style={{
                    color: '#7D8090',
                    fontSize: 20,
                    fontWeight: 'normal',
                    marginBottom: 1
                  }}
                />
                <Typography
                  color="#7D8090"
                  fontFamily="Nunito"
                  fontSize="16px"
                  fontWeight="700"
                  sx={{
                    justifyContent: 'center',
                    textAlign: 'center'
                  }}
                  variant={isMobile ? 'body_100' : 'primary'}
                >
                  Add image
                </Typography>
              </Box>
            </Container>
          );
        }
        return null;
      case 'file':
        return (
          <Container
            sx={{
              color: 'primary.300',
              display: 'grid',
              gridTemplateColumns: '100%'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography color={textColors.DARK_TEXT} variant="body_300">
                {`${t('GENERAL.dragNDrop', [type])}`}
              </Typography>
            </Box>
          </Container>
        );
      default:
        return null;
    }
  };

  const canRemove: boolean = true;

  return (
    <>
      {type === 'image' &&
      (parent === 'profile' || parent === 'company') &&
      content.length > 0 ? (
        false
      ) : (
        <DropzoneLibrary
          PreviewComponent={() => {
            // Para taskDetails, no mostrar preview del dropzone ya que tenemos nuestra propia UI
            if (parent === 'taskDetails') {
              return null;
            }
            // Para otros casos, usar el preview por defecto
            return undefined;
          }}
          accept={parent === 'taskDetails' ? 'image/*' : '*'}
          canRemove={canRemove}
          disabled={disabledDrop}
          initialFiles={intialContent}
          inputContent={inputState()}
          inputWithFilesContent={
            parent === 'taskDetails' ? inputState() : undefined
          }
          maxFiles={multiple ? undefined : 1}
          multiple={multiple}
          onChangeStatus={({ meta, file }: any, status: any) => {
            const idMeta =
              content.filter(
                (fileItem) =>
                  fileItem.name === meta.name && fileItem.size === meta.size
              ).length > 0;

            if (file && file.type !== '') {
              if (type === 'image' && status === 'done') {
                if (parent === 'taskDetails') {
                  // Actualizar content primero y luego llamar setFiles
                  const newContent = [...content, file];
                  setContent(newContent);
                  setFiles(newContent);
                } else {
                  setContent([meta.previewUrl]);
                }
              } else if (status === 'done' && !idMeta) {
                // setContent([...content, file]);
                const newContent = [...content, file];
                setContent(newContent);
                setFiles(newContent);
              } else if (status === 'removed') {
                const auxContent = content.filter((f) => f.name !== file.name);
                setContent(auxContent);
                setFiles([...auxContent]);
              }
            }
          }}
          ref={dropzoneRef}
          styles={{
            dropzone: {
              alignItems: 'center',
              // Exception in color cause its not a material component.
              backgroundColor: mainColors.MAIN_LIGHT,
              border:
                parent === 'taskDetails'
                  ? `1px dashed ${secondaryColors.SECONDARY}`
                  : `1px dashed ${mainColors.MAIN}`,
              display: parent === 'taskDetails' ? 'flex' : 'block',
              height: parent === 'taskDetails' ? 'auto' : '100%',
              justifyContent: 'center',
              overflow: 'hidden',
              padding:
                parent === 'taskDetails' ? '1.5rem' : '32px 10px 32px 10px'
            },
            dropzoneActive: {
              borderColor: mainColors.MAIN
            },
            dropzoneReject: {
              borderColor: '#ff1744'
            },
            inputLabel: {
              color:
                parent === 'taskDetails'
                  ? secondaryColors.SECONDARY
                  : mainColors.MAIN,
              margin: parent === 'taskDetails' ? '0 auto' : 'initial',
              textAlign: 'center',
              width: parent === 'taskDetails' ? '100%' : 'auto'
            },
            preview: {
              display: parent === 'taskDetails' ? 'none' : 'block',
              paddingBottom: '0px',
              paddingLeft: '40px',
              paddingRight: '3%',
              paddingTop: '0px'
            },
            previewImage: {
              display: parent === 'taskDetails' ? 'none' : 'block'
            },
            submitButton: {
              display: 'none'
            },
            submitButtonContainer: {
              display: 'none'
            }
          }}
          submitButtonDisabled={!multiple}
        />
      )}
      {type === 'image' &&
        (parent === 'profile' || parent === 'company') &&
        content.length > 0 && (
          <ImageCropper
            hideCropper={() => {}}
            image={content[0]}
            onCropCancel={() => {
              setContent([]);
            }}
            onCropDone={(newFile: any) => {
              if (callApiOnComplete) {
                profile.setProfilePhoto(newFile).then((data) => {
                  setFiles(data);
                  setContent([]);
                });
              } else {
                setFiles([newFile]);
              }
            }}
          />
        )}
    </>
  );
};

Dropzone.defaultProps = {
  callApiOnComplete: true,
  disabledDrop: false,
  filesList: [],
  multiple: false
};

export default Dropzone;
